import React, { useEffect } from 'react';

// Trust Fund Baby (Sanchit Kedia) · October 2026 monthly report.
// Source of record: TFB Notion Media Tracker (data source 0fd3ac2a-...), read 2026-10-30.
// Views per reel: fill VIEWS (card number -> plays) from Instagram insights; the
// performance section stays hidden until it has data. No projections, no em dashes.

const C = { paper: '#FBFAF6', blue: '#1B3A63', marigold: '#EC9A1E', marigoldText: '#9A5B00', green: '#BFE0C4', sage: '#DDE3DA', soft: '#4B5D77' };
const serif = "'Fraunces', Georgia, serif";
const mono = "'IBM Plex Mono', ui-monospace, monospace";

// card number -> Instagram plays on @sanchitkedia_tfb, Apify reel scrape 30 Oct 2026.
const VIEWS = { 1: 342, 2: 724, 4: 289, 5: 1115, 6: 1464, 7: 409, 8: 704, 9: 1375, 12: 121, 13: 1354, 14: 326, 15: 414, 16: 527, 17: 200, 18: 1373, 19: 1326, 25: 168, 26: 1142, 27: 1184, 28: 1532, 29: 144, 30: 141, 32: 185, 33: 1462, 34: 144 };

const LEARNINGS = [
    ['Rankings travel', 'The one tier list was the best reel of the month at 1,532 views. More tier lists are already lined up for November.'],
    ['Goal maths works', 'The Gift Ladder had the highest median of any repeated format (1,115). The Porsche reel was the second best of the month at 1,464.'],
    ['Warm stories beat money drama', 'Inside the famous-names format, stories about what a parent did for a child did well: Ratan Tata\'s dog, Amitabh, Nita Ambani, Bill Gates, Sundar Pichai, all above 1,100. Debt and collapse stories (Byju\'s, Anil Ambani) stayed under 350. An early pattern, not a rule yet.'],
    ['Instagram is the engine', 'YouTube Shorts carried the same videos and reached 1,204 views across 26 Shorts, so November effort goes where the views are.'],
];

const FORMATS = [
    {
        name: 'Tier list', n: 1,
        what: 'Ranking the usual options on one clear question.',
        why: 'People argue with rankings in the comments, which lifts reach.',
        reels: [[28, 'Whose name is it in?']],
    },
    {
        name: 'The Gift Ladder', n: 3,
        what: 'A big goal for your child and the monthly amount in the hook, with the 12% always labelled as an assumption.',
        why: 'Goal maths is the most shared format in Indian personal finance. We built a compliant version of it.',
        reels: [[5, 'IIT fees for ₹1,100 a month'], [4, 'An IIM seat for ₹3,600 a month'], [6, 'A Porsche at 18 for ₹26,400 a month']],
    },
    {
        name: 'Origin and mindset', n: 3,
        what: 'Why Trust Fund Baby exists, in your words.',
        why: 'Gives new viewers a reason to follow the person, not only the tips.',
        reels: [[1, 'The money thing nobody did for you'], [8, 'The sentence no Indian family says out loud'], [9, 'You are not late, you are unstarted']],
    },
    {
        name: 'Teach: checklists', n: 2,
        what: 'A short checklist a parent can run on their own accounts.',
        why: 'Checklists get saved, and saves tell Instagram to show the reel to more people.',
        reels: [[2, "Five things to check before you put a rupee in your child's name"], [7, 'The Whose-Name Audit']],
    },
    {
        name: 'Famous names in a money moment', n: 11,
        what: 'A well-known name at a hard money moment, then what it means for your child\'s money.',
        why: 'The same hook shape ran far above its page\'s usual views on a finance page we track.',
        reels: [[12, "Narayana Murthy's grandson"], [13, 'Amitabh started again'], [14, "Anil Ambani's personal guarantee"], [15, "Sudha Murty's ₹10,000"], [16, 'The will nobody wrote'], [17, "Byju's ₹17,545 crore"], [18, "Ratan Tata's dog"], [19, "Nita Ambani's ₹5"], [25, "Vaibhav Suryavanshi's ₹1.1 crore"], [26, "Sundar Pichai's plane ticket"], [27, "Bill Gates' under 1%"]],
    },
    {
        name: 'Quick value and what-ifs', n: 4,
        what: 'One sharp idea a parent can use the same day, said fast.',
        why: 'Short, saveable videos that teach without selling.',
        reels: [[30, 'Easy money hacks'], [32, 'What if you die today'], [33, 'The iPhone idea'], [34, 'The cost of waiting']],
    },
    {
        name: 'News', n: 1,
        what: 'A money update that matters this week, explained in under a minute.',
        why: 'Timely topics get pushed while they are fresh.',
        reels: [[29, 'The gold fund SIP pause']],
    },
];

const NOVEMBER = [
    ['Back the winners', 'More tier lists, more Gift Ladders and more warm famous-parent stories. Any reel that runs far above the page\'s usual views gets three follow-ups within 72 hours.'],
    ['Diwali week', 'Gifting is the most natural money-for-children moment of the year. That week leans into gift ideas that last.'],
    ['New ideas lined up', 'The cost of raising a child in your city, a 14-year-old\'s net worth, a new tier list, how Marwadi families talk about money, Diwali gifting, money and happiness, and side income before the year ends.'],
    ['30 reels', 'The same daily rhythm, with your voice on every reel and every caption checked before it goes out.'],
];

const NEEDS = [
    'Instagram insights access (or a weekly screenshot), so we can add saves, shares and follows to views',
    'Who replies to comments and DMs: one owner per inbox, so no parent gets two replies or none',
    "Your team's planned static posts, so topics never repeat within 14 days",
    'Any price or fee that changes before recording day',
];

const Eyebrow = ({ children }) => (
    <div style={{ fontFamily: mono, color: C.marigoldText }} className="text-xs font-medium uppercase tracking-[0.18em]">{children}</div>
);

const Stat = ({ value, label }) => (
    <div className="rounded-2xl p-5 flex flex-col gap-1" style={{ background: '#fff', border: `1px solid ${C.sage}` }}>
        <div style={{ fontFamily: mono, color: C.blue }} className="text-3xl sm:text-4xl font-medium">{value}</div>
        <div className="text-sm" style={{ color: C.soft }}>{label}</div>
    </div>
);

export default function TfbOctoberReport() {
    useEffect(() => {
        document.title = 'Trust Fund Baby | October Report';
        const id = 'tfb-fonts';
        if (!document.getElementById(id)) {
            const l = document.createElement('link');
            l.id = id; l.rel = 'stylesheet';
            l.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Figtree:wght@400;600&family=IBM+Plex+Mono:wght@500&display=swap';
            document.head.appendChild(l);
        }
    }, []);

    const hasViews = Object.keys(VIEWS).length > 0;
    const total = FORMATS.reduce((s, f) => s + f.n, 0);
    const median = (arr) => { const a = [...arr].sort((x, y) => x - y); const m = Math.floor(a.length / 2); return a.length % 2 ? a[m] : Math.round((a[m - 1] + a[m]) / 2); };
    const fmt = (v) => v.toLocaleString('en-IN');

    return (
        <div className="min-h-screen relative" style={{ zIndex: 10000, background: C.paper, color: C.blue, fontFamily: "'Figtree', system-ui, sans-serif" }}>
            <main className="max-w-5xl mx-auto px-4 sm:px-8 py-14 sm:py-20 flex flex-col gap-16">

                <header className="flex flex-col gap-5">
                    <Eyebrow>Trust Fund Baby · Monthly report · October 2026</Eyebrow>
                    <h1 style={{ fontFamily: serif }} className="text-4xl sm:text-6xl font-medium leading-[1.05]">Your first full month, in one page.</h1>
                    <p className="text-lg max-w-3xl" style={{ color: C.soft }}>
                        Everything we posted on @sanchitkedia_tfb in October, the formats we tested, how they did, and what we change in November.
                    </p>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
                        <Stat value={total} label="reels live on Instagram" />
                        <Stat value="18,165" label="Instagram views" />
                        <Stat value="10" label="reels past 1,000 views" />
                        <Stat value={FORMATS.length} label="formats tested" />
                    </div>
                </header>

                <section className="flex flex-col gap-6">
                    <Eyebrow>What we tried</Eyebrow>
                    <h2 style={{ fontFamily: serif }} className="text-3xl sm:text-4xl font-medium">Seven formats in one month.</h2>
                    <p className="max-w-3xl" style={{ color: C.soft }}>A new page needs to find its format fast. So October tested seven, with the most reels going to the one we expected to travel furthest.</p>
                    <div className="flex flex-col gap-4">
                        {FORMATS.map((f) => {
                            const vs = f.reels.map(([no]) => VIEWS[no]).filter((v) => typeof v === 'number');
                            return (
                                <article key={f.name} className="rounded-2xl p-5 sm:p-6 flex flex-col gap-4" style={{ background: '#fff', border: `1px solid ${C.sage}` }}>
                                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                                        <h3 style={{ fontFamily: serif }} className="text-2xl font-medium">{f.name}</h3>
                                        <div style={{ fontFamily: mono }} className="text-sm">
                                            {f.n} {f.n === 1 ? 'reel' : 'reels'}{vs.length ? ` · median ${fmt(median(vs))} views` : ''}
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4 text-sm leading-relaxed">
                                        <div><div style={{ fontFamily: mono, color: C.soft }} className="text-[11px] uppercase tracking-[0.16em] mb-1">The format</div>{f.what}</div>
                                        <div><div style={{ fontFamily: mono, color: C.soft }} className="text-[11px] uppercase tracking-[0.16em] mb-1">Why we tried it</div>{f.why}</div>
                                    </div>
                                    <ul className="flex flex-wrap gap-2">
                                        {f.reels.map(([no, t]) => (
                                            <li key={no} className="rounded-full px-3 py-1 text-sm" style={{ background: C.paper, border: `1px solid ${C.sage}` }}>
                                                <span style={{ fontFamily: mono, color: C.soft }}>{String(no).padStart(2, '0')}</span> {t}
                                                {typeof VIEWS[no] === 'number' && <span style={{ fontFamily: mono }} className="ml-2">{fmt(VIEWS[no])}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            );
                        })}
                    </div>
                </section>

                {hasViews && (
                    <section className="flex flex-col gap-6">
                        <Eyebrow>How they did</Eyebrow>
                        <h2 style={{ fontFamily: serif }} className="text-3xl sm:text-4xl font-medium">Views by format.</h2>
                        <div className="flex flex-col gap-3">
                            {FORMATS.map((f) => {
                                const vs = f.reels.map(([no]) => VIEWS[no]).filter((v) => typeof v === 'number');
                                if (!vs.length) return null;
                                const max = Math.max(...FORMATS.flatMap((g) => g.reels.map(([no]) => VIEWS[no] || 0)));
                                const m = median(vs);
                                return (
                                    <div key={f.name} className="grid grid-cols-[minmax(0,1fr)_2fr] gap-3 items-center text-sm">
                                        <div>{f.name}</div>
                                        <div className="flex items-center gap-2">
                                            <div className="h-4 rounded" style={{ width: `${Math.max(4, (m / max) * 100)}%`, background: C.blue }} />
                                            <span style={{ fontFamily: mono }}>{fmt(m)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-sm" style={{ color: C.soft }}>Median Instagram views per reel in each format, read on 30 October. The last three reels went out 27 to 29 October and are still collecting views, so the quick-value format will look better next week.</p>
                    </section>
                )}

                <section className="flex flex-col gap-6">
                    <Eyebrow>What the numbers say</Eyebrow>
                    <h2 style={{ fontFamily: serif }} className="text-3xl sm:text-4xl font-medium">Four things October taught us.</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {LEARNINGS.map(([t, b]) => (
                            <div key={t} className="rounded-2xl p-5" style={{ background: '#fff', border: `1px solid ${C.sage}` }}>
                                <h3 style={{ fontFamily: serif }} className="text-xl font-medium mb-1">{t}</h3>
                                <p className="text-sm leading-relaxed" style={{ color: C.soft }}>{b}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="flex flex-col gap-6">
                    <Eyebrow>Behind every reel</Eyebrow>
                    <h2 style={{ fontFamily: serif }} className="text-3xl sm:text-4xl font-medium">What stays the same on every video.</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {[
                            ['Your voice', 'Every reel is in your voice, never a stock voice.'],
                            ['Checked against the AMFI rules', 'No return promises and no urgency. Every caption carries the full disclaimer with your ARN.'],
                            ['One look', 'The Trust Fund Baby colours and type on every reel, so the page reads as one brand.'],
                            ['An empty lane', 'Across nearly 200 posts from the biggest parenting and money pages, almost nobody talks about money for children. That space is open for you.'],
                        ].map(([t, b]) => (
                            <div key={t} className="rounded-2xl p-5" style={{ background: '#fff', border: `1px solid ${C.sage}` }}>
                                <h3 style={{ fontFamily: serif }} className="text-xl font-medium mb-1">{t}</h3>
                                <p className="text-sm leading-relaxed" style={{ color: C.soft }}>{b}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="flex flex-col gap-6">
                    <Eyebrow>November</Eyebrow>
                    <h2 style={{ fontFamily: serif }} className="text-3xl sm:text-4xl font-medium">What we do next.</h2>
                    <ol className="flex flex-col gap-3">
                        {NOVEMBER.map(([t, b], i) => (
                            <li key={t} className="rounded-2xl p-5 flex gap-4" style={{ background: '#fff', border: `1px solid ${C.sage}` }}>
                                <span style={{ fontFamily: mono, color: C.marigoldText }} className="text-lg">{String(i + 1).padStart(2, '0')}</span>
                                <div>
                                    <h3 className="font-semibold">{t}</h3>
                                    <p className="text-sm leading-relaxed mt-1" style={{ color: C.soft }}>{b}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </section>

                <section className="flex flex-col gap-5 rounded-3xl p-6 sm:p-8" style={{ background: C.blue, color: C.paper }}>
                    <div style={{ fontFamily: mono, color: C.marigold }} className="text-xs font-medium uppercase tracking-[0.18em]">What we need from you</div>
                    <h2 style={{ fontFamily: serif }} className="text-3xl font-medium">Four things for a stronger November.</h2>
                    <ul className="flex flex-col gap-3">
                        {NEEDS.map((n) => (
                            <li key={n} className="flex gap-3 items-start">
                                <span className="mt-2 h-2 w-2 shrink-0 rounded-full" style={{ background: C.marigold }} />
                                <span>{n}</span>
                            </li>
                        ))}
                    </ul>
                </section>

                <footer style={{ fontFamily: mono, color: C.soft }} className="text-xs">
                    Prepared by SocialSEO for Trust Fund Baby · 30 October 2026
                </footer>
            </main>
        </div>
    );
}
